import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCopy, FiShare2, FiMail, FiMessageSquare } = FiIcons;

const ShareModal = ({ project, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !project) return null;

  // Generate interview URL
  const interviewUrl = `${window.location.origin}/#/interview/${project.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(interviewUrl);
      setCopied(true);
      toast.success('¡Enlace copiado al portapapeles!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Error al copiar el enlace');
    }
  };

  const handleEmailShare = () => {
    const subject = `Oportunidad laboral: ${project.title} - ${project.company}`;
    const body = `¡Hola!

Te comparto una excelente oportunidad laboral en ${project.company}:

Posición: ${project.title}
Empresa: ${project.company}

Para aplicar y realizar tu entrevista, visita el siguiente enlace:
${interviewUrl}

¡Esperamos conocerte pronto!

Saludos,
Equipo de ${project.company}`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handleWhatsAppShare = () => {
    const message = `🚀 ¡Oportunidad laboral en ${project.company}!
📋 Posición: ${project.title}
🏢 Empresa: ${project.company}

Para aplicar y realizar tu entrevista, visita:
${interviewUrl}

¡No pierdas esta oportunidad!`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShare2} className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Compartir Oportunidad</h3>
                <p className="text-sm text-gray-600">{project.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Project Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">{project.title}</h4>
            <p className="text-gray-600 text-sm mb-2">{project.company}</p>
            <p className="text-gray-700 text-sm">{project.description}</p>
          </div>

          {/* URL Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enlace de la entrevista
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={interviewUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  copied ? 'bg-green-100 text-green-700' : 'bg-primary text-white hover:bg-blue-700'
                }`}
              >
                <SafeIcon icon={FiCopy} className="w-4 h-4" />
                <span className="text-sm">{copied ? 'Copiado' : 'Copiar'}</span>
              </button>
            </div>
          </div>

          {/* QR Code */}
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Código QR</h4>
            <div className="inline-block p-4 bg-white border border-gray-200 rounded-lg">
              <QRCode size={120} value={interviewUrl} viewBox="0 0 256 256" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Escanea este código para acceder directamente
            </p>
          </div>

          {/* Share Options */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Compartir por:</h4>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleEmailShare}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
                <span className="font-medium">Email</span>
              </button>
              <button
                onClick={handleWhatsAppShare}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
              >
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
                <span className="font-medium">WhatsApp</span>
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Instrucciones para candidatos:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Haz clic en el enlace o escanea el código QR</li>
              <li>• Completa tus datos personales</li>
              <li>• Sube tu CV (si es requerido)</li>
              <li>• Responde las preguntas de video</li>
              <li>• ¡Listo! Recibirás una confirmación</li>
            </ul>
          </div>

          {/* Statistics */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Estadísticas del proyecto:
            </h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{project.responses || 0}</p>
                <p className="text-xs text-gray-600">Respuestas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{project.questions?.length || 0}</p>
                <p className="text-xs text-gray-600">Preguntas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{project.responseTime || 60}s</p>
                <p className="text-xs text-gray-600">Por respuesta</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ShareModal;